// wcwb/frontend/src/views/EventDetail.js
import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { S } from '../components/styles';
import SuggestDishModal from '../components/SuggestDishModal';
import SpoonacularModal from '../components/SpoonacularModal';
import MenuBalance from '../components/MenuBalance';
import Stars from '../components/Stars';
import APBPanel from '../components/APBPanel';
import EventPhotos from '../components/EventPhotos';
import CommentsThread from '../components/CommentsThread';
import EditEventModal from '../components/EditEventModal';
import HostDashboardPanel from '../components/HostDashboardPanel';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
const CATS = ['Starter','Main','Side','Dessert','Drinks','Bread'];
const fmtDate = d => new Date(d).toLocaleDateString('en-US',{weekday:'long',month:'long',day:'numeric',year:'numeric'});
const fmtDateShort = d => new Date(d).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'});
const AVATAR_COLORS = ['#C4612A','#5D4037','#1A237E','#1B5E20','#BF360C','#4A148C'];

function avgRating(dish) {
  const rs = dish.dish_ratings || [];
  return rs.length ? rs.reduce((a,r)=>a+r.rating,0)/rs.length : 0;
}
function Avatar({ name, id, size=34 }) {
  const initials = name?.split(' ').map(w=>w[0]).join('').toUpperCase()||'?';
  const bg = AVATAR_COLORS[(id||'').charCodeAt(0)%AVATAR_COLORS.length]||'#C4612A';
  return <div style={{width:size,height:size,borderRadius:'50%',background:bg,display:'flex',alignItems:'center',justifyContent:'center',color:'white',fontSize:size*.36,fontWeight:500,flexShrink:0}}>{initials}</div>;
}
function StatusTag({ status }) {
  const map = { pending:['#FFF3E0','#BF360C','🕐 Pending'], approved:['#E8F5E9','#1B5E20','✓ Approved'], declined:['#FFEBEE','#B71C1C','✕ Declined'] };
  const [bg,color,label] = map[status]||['#F0EAE0','#7A6555',status];
  return <span style={{display:'inline-block',padding:'3px 8px',borderRadius:12,fontSize:11,fontWeight:500,background:bg,color}}>{label}</span>;
}

function DishCard({ dish, isHost, isPast, myRating, onApprove, onDecline, onRate, onEdit }) {
  const [expanded, setExpanded] = useState(false);
  const a = avgRating(dish);
  const rCount = (dish.dish_ratings||[]).length;
  return (
    <div style={{background:'white',borderRadius:12,border:'1px solid #E5D9C8',padding:18,marginBottom:10}}>
      <div style={{display:'flex',gap:10,marginBottom:8}}>
        <Avatar name={dish.profiles?.full_name||'?'} id={dish.guest_id} size={32}/>
        <div style={{flex:1}}>
          <div style={{display:'flex',gap:8,alignItems:'flex-start'}}>
            <h4 style={{fontFamily:'Cormorant Garamond,serif',fontSize:19,flex:1}}>{dish.name}</h4>
            <StatusTag status={dish.status}/>
          </div>
          <p style={{fontSize:12,color:'#7A6555'}}>{dish.profiles?.full_name} · {dish.method}</p>
        </div>
      </div>
      {dish.description && <p style={{fontSize:14,color:'#5A3E2B',lineHeight:1.55,marginBottom:8}}>{dish.description}</p>}

      {/* Guest can edit their own dish */}
      {onEdit && (
        <button onClick={onEdit} style={{background:'none',border:'none',cursor:'pointer',color:'#C4612A',fontSize:12,fontWeight:500,marginBottom:8}}>
          ✏️ Edit my dish
        </button>
      )}

      {isHost && dish.status==='pending' && (
        <div style={{display:'flex',gap:8,paddingTop:12,borderTop:'1px solid #E5D9C8',marginTop:8}}>
          <button style={{...S.btnSage,padding:'7px 16px',fontSize:13}} onClick={onApprove}>✓ Approve</button>
          <button style={{background:'#FFEBEE',color:'#B71C1C',border:'none',borderRadius:8,padding:'7px 16px',fontSize:13,cursor:'pointer'}} onClick={onDecline}>Decline</button>
        </div>
      )}
      {isPast && dish.status==='approved' && (
        <div style={{paddingTop:10,marginTop:10,borderTop:'1px solid #E5D9C8'}}>
          <div style={{display:'flex',alignItems:'center',gap:10}}>
            <span style={{fontSize:13,color:'#7A6555'}}>Your rating:</span>
            <Stars value={myRating} onChange={onRate} readonly={myRating>0} size={18}/>
            {rCount>0 && <span style={{fontSize:12,color:'#7A6555',marginLeft:'auto'}}>{a.toFixed(1)} avg · {rCount} votes</span>}
          </div>
        </div>
      )}
      {dish.recipe && (
        <div style={{marginTop:10}}>
          <button onClick={()=>setExpanded(!expanded)} style={{background:'none',border:'none',cursor:'pointer',color:'#C4612A',fontSize:13,fontWeight:500,display:'flex',alignItems:'center',gap:5}}>
            📖 {expanded?'Hide':'View'} recipe
          </button>
          {expanded && <div style={{marginTop:10,background:'#FAF7F2',borderRadius:10,padding:14,fontSize:13,color:'#5A3E2B',lineHeight:1.65,fontStyle:'italic',borderLeft:'3px solid #C4612A'}}>{dish.recipe}</div>}
        </div>
      )}
    </div>
  );
}

function MenuNudge({ dishes }) {
  const approved = dishes.filter(d => d.status === 'approved' || d.status === 'pending');
  const covered  = new Set(approved.map(d => d.category));
  const missing  = ['Starter','Main','Dessert'].filter(c => !covered.has(c));
  if (missing.length === 0) return null;
  return (
    <div style={{background:'#FDF6E3',border:'1px solid rgba(212,160,23,.3)',borderRadius:12,padding:'12px 16px',marginBottom:16,display:'flex',alignItems:'center',gap:10}}>
      <span style={{fontSize:20}}>💡</span>
      <p style={{fontSize:13,color:'#7a5500'}}>
        <strong>Still needed:</strong> {missing.join(', ')} — consider bringing one of these!
      </p>
    </div>
  );
}

function CancelAttendanceModal({ onClose, onConfirm }) {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  return (
    <div style={{position:'fixed',inset:0,background:'rgba(44,26,14,.55)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:300,padding:16}} onClick={onClose}>
      <div style={{background:'white',borderRadius:24,width:'100%',maxWidth:460,padding:32}} onClick={e=>e.stopPropagation()}>
        <h3 style={{fontFamily:'Cormorant Garamond,serif',fontSize:24,marginBottom:8}}>Cancel attendance</h3>
        <p style={{fontSize:14,color:'#7A6555',marginBottom:20}}>The host will be notified. You can let them know why if you'd like.</p>
        <label style={S.label}>Reason (optional)</label>
        <textarea
          style={{width:'100%',padding:'10px 13px',border:'1px solid #E5D9C8',borderRadius:10,fontFamily:'Jost,sans-serif',fontSize:14,color:'#2C1A0E',outline:'none',marginBottom:16,resize:'vertical',minHeight:80}}
          placeholder="e.g. Something came up, so sorry!"
          value={reason} onChange={e=>setReason(e.target.value)}/>
        <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
          <button style={S.btnGhost} onClick={onClose}>Keep my spot</button>
          <button
            style={{...S.btnPrimary,background:'#B71C1C',opacity:loading?0.5:1}}
            disabled={loading}
            onClick={async()=>{setLoading(true);await onConfirm(reason);setLoading(false);}}>
            {loading?'Cancelling…':'Yes, cancel my attendance'}
          </button>
        </div>
      </div>
    </div>
  );
}

function GuestsTab({ event, isHost, onAddGuest, onCancelAttendance, currentUserId, token, onRefresh }) {
  const [showAdd, setShowAdd]     = useState(false);
  const [email, setEmail]         = useState('');
  const [adding, setAdding]       = useState(false);
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState('');
  const [showCancel, setShowCancel] = useState(false);

  async function handleAdd() {
    if (!email.trim()) return;
    setAdding(true); setError(''); setSuccess('');
    try { await onAddGuest(event.id, email.trim()); setSuccess(`Invite sent to ${email}`); setEmail(''); setShowAdd(false); }
    catch(e) { setError(e.message); }
    finally { setAdding(false); }
  }

  const all = [
    { id:event.host_id, name:event.profiles?.full_name, role:'Host' },
    ...(event.event_guests||[]).map(g=>({
      id:g.user_id, name:g.profiles?.full_name, role:'Guest',
      status:g.status, dietary:g.profiles?.dietary, skills:g.profiles?.skills,
    }))
  ];

  const myGuestRow = (event.event_guests||[]).find(g=>g.user_id===currentUserId);
  const isCancelled = myGuestRow?.status === 'declined';

  return (
    <div>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12,marginBottom:20}}>
        {[
        {label:'Total guests',   val:all.length},
        {label:'Confirmed',      val:(event.event_guests||[]).filter(g=>g.status==='accepted').length+1},
        {label:'Total attending', val:(event.event_guests||[]).filter(g=>g.status==='accepted').reduce((a,g)=>a+(g.plus_ones||0),0) + (event.event_guests||[]).filter(g=>g.status==='accepted').length + 1},
        {label:'Dishes confirmed', val:(event.dishes||[]).filter(d=>d.status==='approved').length},
      ].map(s=>(
          <div key={s.label} style={{...S.card,...S.cardPad,textAlign:'center',padding:14}}>
            <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:28}}>{s.val}</div>
            <div style={{fontSize:11,color:'#7A6555',marginTop:2}}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Cancel attendance — guests only */}
      {!isHost && myGuestRow && !isCancelled && event.status === 'upcoming' && (
        <div style={{marginBottom:20}}>
          <button onClick={()=>setShowCancel(true)}
            style={{background:'none',border:'1px solid #FFCDD2',color:'#B71C1C',borderRadius:10,padding:'8px 16px',fontFamily:'Jost,sans-serif',fontSize:13,cursor:'pointer'}}>
            I can no longer attend
          </button>
        </div>
      )}
      {isCancelled && (
        <div style={{background:'#FFEBEE',border:'1px solid #FFCDD2',borderRadius:10,padding:'12px 16px',marginBottom:20,fontSize:13,color:'#B71C1C'}}>
          You have cancelled your attendance at this event.
        </div>
      )}

      {isHost && (
        <div style={{marginBottom:20}}>
          {!showAdd ? (
            <button style={S.btnPrimary} onClick={()=>setShowAdd(true)}>+ Invite another guest</button>
          ) : (
            <div style={{...S.card,...S.cardPad,borderLeft:'3px solid #C4612A'}}>
              <p style={{fontWeight:500,marginBottom:12}}>Invite a guest by email</p>
              {error   && <div style={{background:'#FFEBEE',color:'#B71C1C',padding:'8px 12px',borderRadius:8,fontSize:13,marginBottom:10}}>{error}</div>}
              {success && <div style={{background:'#E8F5E9',color:'#1B5E20',padding:'8px 12px',borderRadius:8,fontSize:13,marginBottom:10}}>{success}</div>}
              <div style={{display:'flex',gap:8}}>
                <input style={{...S.input,marginBottom:0,flex:1}} placeholder="guest@email.com" type="email"
                  value={email} onChange={e=>setEmail(e.target.value)} onKeyDown={e=>e.key==='Enter'&&handleAdd()} autoFocus/>
                <button style={{...S.btnPrimary,opacity:(!email||adding)?0.5:1}} disabled={!email||adding} onClick={handleAdd}>
                  {adding?'Sending…':'Send invite'}
                </button>
                <button style={S.btnGhost} onClick={()=>{setShowAdd(false);setError('');setSuccess('');}}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      <div style={{display:'flex',flexDirection:'column',gap:10}}>
        {all.map(p=>{
          const dish=(event.dishes||[]).find(d=>d.guest_id===p.id);
          const guestRow=(event.event_guests||[]).find(g=>g.user_id===p.id);
          return (
            <div key={p.id} style={{...S.card,...S.cardPad,display:'flex',alignItems:'center',gap:14,opacity:guestRow?.status==='declined'?0.6:1}}>
              <Avatar name={p.name||'?'} id={p.id} size={42}/>
              <div style={{flex:1}}>
                <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:4,flexWrap:'wrap'}}>
                  <p style={{fontWeight:500}}>{p.name||'Invited user'}{p.id===currentUserId?' (you)':''}</p>
                  <span style={{padding:'2px 8px',borderRadius:12,fontSize:11,background:p.role==='Host'?'#FAEEE8':'#F0EAE0',color:p.role==='Host'?'#7B2D0D':'#7A6555'}}>{p.role}</span>
                  {guestRow?.status==='declined' && <span style={{padding:'2px 8px',borderRadius:12,fontSize:11,background:'#FFEBEE',color:'#B71C1C'}}>Cancelled</span>}
                  {guestRow?.status==='accepted' && <span style={{padding:'2px 8px',borderRadius:12,fontSize:11,background:'#E8F5E9',color:'#1B5E20'}}>Confirmed</span>}
                </div>
                {p.dietary?.length>0&&<div style={{display:'flex',gap:4,flexWrap:'wrap',marginBottom:4}}>{p.dietary.map(d=><span key={d} style={{fontSize:11,padding:'2px 6px',borderRadius:6,background:'#E8F5E9',color:'#1B5E20'}}>{d}</span>)}</div>}
                {p.skills?.length>0&&<p style={{fontSize:11,color:'#7A6555'}}>{p.skills.join(' · ')}</p>}
                {guestRow?.cancel_reason && <p style={{fontSize:11,color:'#7A6555',fontStyle:'italic'}}>"{guestRow.cancel_reason}"</p>}
                {guestRow?.status==='accepted' && p.id===currentUserId && (
                  <div style={{display:'flex',alignItems:'center',gap:8,marginTop:6}}>
                    <span style={{fontSize:11,color:'#7A6555'}}>People attending:</span>
                    <button
                      style={{width:22,height:22,borderRadius:'50%',border:'1px solid #E5D9C8',background:'white',fontSize:14,cursor:(guestRow.plus_ones||0)===0?'not-allowed':'pointer',color:(guestRow.plus_ones||0)===0?'#E5D9C8':'#2C1A0E',display:'flex',alignItems:'center',justifyContent:'center',padding:0}}
                      disabled={(guestRow.plus_ones||0)===0}
                      onClick={()=>updatePlusOnes(event.id,(guestRow.plus_ones||0)-1)}>
                      −
                    </button>
                    <span style={{fontSize:13,fontWeight:500,minWidth:16,textAlign:'center'}}>{(guestRow.plus_ones||0)+1}</span>
                    <button
                      style={{width:22,height:22,borderRadius:'50%',border:'1px solid #E5D9C8',background:'white',fontSize:14,cursor:'pointer',color:'#2C1A0E',display:'flex',alignItems:'center',justifyContent:'center',padding:0}}
                      onClick={()=>updatePlusOnes(event.id,(guestRow.plus_ones||0)+1)}>
                      +
                    </button>
                    <span style={{fontSize:11,color:'#7A6555'}}>{(guestRow.plus_ones||0)===0?'Just you':`You + ${guestRow.plus_ones}`}</span>
                  </div>
                )}
                {guestRow?.status==='accepted' && p.id!==currentUserId && (guestRow.plus_ones||0)>0 && (
                  <p style={{fontSize:11,color:'#7A6555',marginTop:4}}>+{guestRow.plus_ones} guest{guestRow.plus_ones>1?'s':''}</p>
                )}
              </div>
              <div style={{textAlign:'right',flexShrink:0}}>
                {dish?<><p style={{fontSize:13,fontWeight:500}}>{dish.name}</p><StatusTag status={dish.status}/></>
                  :p.role==='Guest'&&!guestRow?.status!=='declined'&&<span style={{fontSize:11,padding:'4px 10px',borderRadius:20,background:'#FFF3E0',color:'#BF360C'}}>No dish yet</span>}
              </div>
            </div>
          );
        })}
      </div>

      {showCancel && (
        <CancelAttendanceModal
          onClose={()=>setShowCancel(false)}
          onConfirm={async(reason)=>{ await onCancelAttendance(event.id, reason); setShowCancel(false); }}
        />
      )}
    </div>
  );
}

function HallOfFame({ dishes, currentUserId, onRate }) {
  const rated   = dishes.filter(d=>d.status==='approved'&&(d.dish_ratings||[]).length>0);
  const unrated = dishes.filter(d=>d.status==='approved'&&!(d.dish_ratings||[]).length);
  const sorted  = [...rated].sort((a,b)=>avgRating(b)-avgRating(a));
  const podOrder   = sorted.length>=3?[sorted[1],sorted[0],sorted[2]]:sorted;
  const heights    = sorted.length>=3?[100,144,80]:sorted.map((_,i)=>[144,100][i]||80);
  const medals     = sorted.length>=3?['🥈','🥇','🥉']:['🥇','🥈'];
  const barColors  = ['#7A6555','#C4612A','#B0977A'];
  return (
    <div>
      {unrated.length>0&&(
        <div style={{...S.card,...S.cardPad,borderLeft:'3px solid #C4612A',marginBottom:20}}>
          <p style={{fontSize:13,fontWeight:500,color:'#C4612A',marginBottom:12}}>Rate these dishes</p>
          {unrated.map(d=>(
            <div key={d.id} style={{display:'flex',alignItems:'center',gap:10,marginBottom:10}}>
              <span style={{flex:1,fontSize:13}}>{d.name}</span>
              <Stars value={(d.dish_ratings||[]).find(r=>r.user_id===currentUserId)?.rating||0} onChange={s=>onRate(d.id,s)} size={18}/>
            </div>
          ))}
        </div>
      )}
      {sorted.length>0&&(
        <>
          <div style={{...S.card,...S.cardPad,marginBottom:20}}>
            <p style={{fontWeight:500,textAlign:'center',marginBottom:10}}>Most favoured dishes</p>
            <div style={{display:'flex',alignItems:'flex-end',justifyContent:'center',gap:10,paddingTop:20}}>
              {podOrder.filter(Boolean).map((dish,i)=>(
  <div key={dish.id} style={{display:'flex',flexDirection:'column',alignItems:'center',gap:6}}>
                  {i===1&&sorted.length>=3&&<div style={{fontSize:24}}>👑</div>}
                  <div style={{width:88,height:heights[i],borderRadius:'10px 10px 0 0',background:barColors[i],display:'flex',alignItems:'center',justifyContent:'center',fontSize:28}}>{medals[i]}</div>
                  <div style={{fontSize:13,fontWeight:500}}>{avgRating(dish).toFixed(1)} ★</div>
                  <div style={{fontSize:12,color:'#7A6555',textAlign:'center',maxWidth:96,lineHeight:1.3}}>{dish.name}</div>
                  <div style={{fontSize:11,color:'#7A6555'}}>{dish.profiles?.full_name}</div>
                </div>
              ))}
            </div>
          </div>
          <div style={{...S.card,...S.cardPad}}>
            <p style={{fontWeight:500,marginBottom:14}}>Rating breakdown</p>
            {sorted.map((d,i)=>{
              const a=avgRating(d);
              return (
                <div key={d.id} style={{marginBottom:14}}>
                  <div style={{display:'flex',gap:8,marginBottom:6,alignItems:'center'}}>
                    <span style={{fontSize:14}}>{['🥇','🥈','🥉'][i]||'·'}</span>
                    <span style={{fontSize:13,fontWeight:500,flex:1}}>{d.name}</span>
                    <span style={{fontSize:12,color:'#7A6555'}}>{a.toFixed(1)} / 5 · {(d.dish_ratings||[]).length} votes</span>
                  </div>
                  <div style={{height:8,background:'#E5D9C8',borderRadius:4,overflow:'hidden'}}>
                    <div style={{height:'100%',width:`${(a/5)*100}%`,background:a>=4.5?'#C4612A':a>=3.5?'#D4A017':'#7A6555',borderRadius:4,transition:'width .6s ease'}}/>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
      {sorted.length===0&&unrated.length===0&&<div style={S.empty}><div style={{fontSize:52,marginBottom:16}}>🏆</div><h3 style={{fontFamily:'Cormorant Garamond,serif',fontSize:24}}>No dishes yet</h3></div>}
    </div>
  );
}

function WishlistAddForm({ eventId, onAdd, refresh }) {
  const [description, setDescription] = useState('');
  const [adding, setAdding] = useState(false);

  async function updatePlusOnes(eventId, newCount) {
    try {
      const res = await fetch(`${API}/attendance/${eventId}/plus-ones`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ plus_ones: newCount }),
      });
      if (!res.ok) throw new Error('Failed to update');
      await onRefresh();
    } catch(e) { console.error(e); }
  }

  async function handleAdd() {
    if (!description.trim()) return;
    setAdding(true);
    try {
      await onAdd(eventId, description.trim());
      setDescription('');
      await refresh();
    } catch(e) { console.error(e); }
    finally { setAdding(false); }
  }

  return (
    <div style={{display:'flex',gap:8,marginBottom:16}}>
      <input
        style={{flex:1,padding:'9px 13px',border:'1px solid #E5D9C8',borderRadius:10,fontFamily:'Jost,sans-serif',fontSize:13,color:'#2C1A0E',outline:'none'}}
        placeholder="e.g. A nice bottle of red, Something sweet with honey…"
        value={description}
        onChange={e=>setDescription(e.target.value)}
        onKeyDown={e=>e.key==='Enter'&&handleAdd()}
      />
      <button
        style={{background:'#C4612A',color:'white',border:'none',borderRadius:10,padding:'9px 16px',fontFamily:'Jost,sans-serif',fontSize:13,fontWeight:500,cursor:'pointer',opacity:(!description.trim()||adding)?0.5:1}}
        disabled={!description.trim()||adding}
        onClick={handleAdd}>
        {adding?'Adding…':'+ Add'}
      </button>
    </div>
  );
}

export default function EventDetail({ event, onBack, eventsHook }) {
  const { profile, token } = useAuth();
  const { approveDish, declineDish, suggestDish, rateDish, claimWishlistItem, addGuest, updateEvent, refresh } = eventsHook;
  const [tab, setTab]               = useState('dishes');
  const [showSuggest, setShowSuggest] = useState(false);
  const [showSpoon, setShowSpoon]     = useState(false);
  const [showEdit, setShowEdit]       = useState(false);
  const [instaPrefill, setInstaPrefill] = useState(null);
  const [showMemory, setShowMemory]   = useState(false);
  const [editingDish, setEditingDish] = useState(null);
const [memory, setMemory]           = useState(null);
const [generatingMemory, setGeneratingMemory] = useState(false);

useEffect(() => {
  if (isPast) fetchMemory();
}, [event.id]);

async function fetchMemory() {
  try {
    const res = await fetch(`${API}/memories/${event.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) setMemory(await res.json());
  } catch(e) { console.error(e); }
}

async function generateMemory() {
  setGeneratingMemory(true);
  try {
    const res = await fetch(`${API}/memories/${event.id}/generate`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error);
    setMemory(data);
  } catch(e) {
    alert(e.message);
  } finally {
    setGeneratingMemory(false);
  }
}

  const isHost  = event.host_id === profile?.id;
  const isGuest = (event.event_guests||[]).some(g=>g.user_id===profile?.id);
  const myDish  = (event.dishes||[]).find(d=>d.guest_id===profile?.id);
  const isPast  = event.status === 'past';
  const pending = (event.dishes||[]).filter(d=>d.status==='pending');
  const deadlinePassed = event.suggest_deadline && new Date(event.suggest_deadline) < new Date();

  const tabs = [
  { id:'dishes',   label:'Dishes' },
  { id:'guests',   label:'Guests' },
  { id:'chat',     label:'💬 Chat' },
  { id:'photos',   label:'📷 Photos' },
  ...(isHost ? [{ id:'balance', label:'Menu Balance' }] : []),
  ...(isHost ? [{ id:'host', label:'🏠 Host Tools' }] : []),
  ...(isHost && !isPast ? [{ id:'wishlist', label:'Wishlist' }] : []),
  ...(!isPast ? [{ id:'apb', label:'🚨 APB' }] : []),
  ...(isPast ? [{ id:'hof', label:'🏆 Hall of Fame' }] : []),
];

  async function handleSuggest(form) { await suggestDish(event.id, form); }

  async function handleCancelAttendance(eventId, reason) {
    await fetch(`${API}/attendance/${eventId}/cancel`, {
      method: 'PATCH',
      headers: { 'Content-Type':'application/json', Authorization:`Bearer ${token}` },
      body: JSON.stringify({ reason }),
    });
    await refresh();
  }

  async function handleEditDish(form) {
    if (!editingDish) return;
    await fetch(`${API}/dishes/${editingDish.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type':'application/json', Authorization:`Bearer ${token}` },
      body: JSON.stringify(form),
    });
    await refresh();
    setEditingDish(null);
  }

  return (
    <div>
      {/* Hero */}
      <div style={{background:'#2C1A0E',padding:'28px 36px 24px',color:'#FAF7F2'}}>
        <div style={{display:'flex',gap:8,marginBottom:18,flexWrap:'wrap'}}>
          <button onClick={onBack} style={{background:'rgba(250,247,242,.1)',border:'none',color:'rgba(250,247,242,.8)',padding:'6px 14px',borderRadius:8,cursor:'pointer',fontFamily:'Jost,sans-serif',fontSize:13,display:'inline-flex',alignItems:'center',gap:6}}>← Back</button>
          {isHost && <button onClick={()=>setShowEdit(true)} style={{background:'rgba(250,247,242,.1)',border:'none',color:'rgba(250,247,242,.8)',padding:'6px 14px',borderRadius:8,cursor:'pointer',fontFamily:'Jost,sans-serif',fontSize:13}}>✏️ Edit event</button>}
        </div>
        <div style={{display:'flex',gap:16,flexWrap:'wrap',alignItems:'flex-start'}}>
          <span style={{fontSize:52}}>{event.emoji}</span>
          <div style={{flex:1}}>
            <h2 style={{fontFamily:'Cormorant Garamond,serif',fontSize:28,color:'#FAF7F2',marginBottom:4}}>{event.title}</h2>
            <p style={{color:'rgba(250,247,242,.6)',fontSize:13}}>{fmtDate(event.date)} · {event.time}</p>
            <p style={{color:'rgba(250,247,242,.5)',fontSize:13,marginTop:2}}>📍 {event.address} · Hosted by {event.profiles?.full_name}</p>
          </div>
          {/* Memory Card removed — replaced by Memory PDF */}
{isPast && isHost && !memory && (
  <button
    style={{...S.btnPrimary, background:'#5B7A5B', fontSize:13, padding:'8px 16px', opacity: generatingMemory ? 0.6 : 1}}
    disabled={generatingMemory}
    onClick={generateMemory}>
    {generatingMemory ? '⏳ Generating PDF…' : '📄 Generate Memory PDF'}
  </button>
)}
{isPast && memory && (
  
    <a href={memory.pdf_url}
    target="_blank"
    rel="noopener noreferrer"
    style={{...S.btnPrimary, background:'#5B7A5B', fontSize:13, padding:'8px 16px', textDecoration:'none', display:'inline-flex', alignItems:'center', gap:6}}>
    📥 Download Memory PDF
  </a>
)}
{isPast && isHost && memory && (
  <button
    style={{
      background: 'rgba(250,247,242,.15)',
      border: '1px solid rgba(250,247,242,.15)',
      color: '#FAF7F2',
      borderRadius: 8,
      padding: '8px 16px',
      fontSize: 13,
      fontFamily: 'Jost,sans-serif',
      cursor: generatingMemory ? 'not-allowed' : 'pointer',
      opacity: generatingMemory ? 0.6 : 1,
    }}
    disabled={generatingMemory}
    onClick={generateMemory}>
    {generatingMemory ? '⏳ Regenerating…' : '🔄 Regenerate PDF'}
  </button>
)}
        </div>
        {event.theme&&<div style={{background:'rgba(196,97,42,.2)',border:'1px solid rgba(196,97,42,.35)',borderRadius:10,padding:'10px 14px',marginTop:14,fontSize:13,color:'rgba(250,247,242,.85)',fontStyle:'italic'}}>🎨 {event.theme}</div>}
      </div>

      <div style={{padding:'24px 36px 120px'}}>
        {/* Action bar */}
        <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:20}}>
          {(isGuest||isHost) && !myDish && !isPast && !deadlinePassed && (
  <>
    <button style={S.btnPrimary} onClick={()=>setShowSuggest(true)}>+ Suggest a dish</button>
    <button style={{...S.btnPrimary,background:'#2E7D32',border:'none'}} onClick={()=>setShowSpoon(true)}>🍳 Find a recipe</button>
  </>
)}
{deadlinePassed && !myDish && !isPast && (
  <span style={{fontSize:13,color:'#7A6555',fontStyle:'italic'}}>🔒 Dish suggestions are now closed for this event</span>
)}
          {pending.length>0&&isHost&&<span style={{display:'inline-flex',alignItems:'center',gap:4,padding:'4px 10px',borderRadius:20,fontSize:12,fontWeight:500,background:'#FFF3E0',color:'#BF360C'}}>⏳ {pending.length} pending</span>}
        </div>

        {/* Tabs */}
        <div style={{display:'flex',gap:3,background:'#F0EAE0',padding:4,borderRadius:12,marginBottom:24,flexWrap:'wrap'}}>
          {tabs.map(t=>(
            <button key={t.id} onClick={()=>setTab(t.id)}
              style={{flex:1,padding:'8px',borderRadius:9,border:'none',background:tab===t.id?'white':'transparent',cursor:'pointer',fontFamily:'Jost,sans-serif',fontSize:13,fontWeight:500,color:tab===t.id?'#2C1A0E':'#7A6555',boxShadow:tab===t.id?'0 1px 4px rgba(44,26,14,.1)':'none',transition:'all .15s',minWidth:60}}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Dishes */}
        {tab==='dishes'&&(
          <div>
            {/* Menu nudge for guests who haven't suggested yet */}
            {!myDish && !isPast && (isGuest||isHost) && (
              <MenuNudge dishes={event.dishes||[]}/>
            )}
            {CATS.map(cat=>{
              const catDishes=(event.dishes||[]).filter(d=>d.category===cat);
              if(!catDishes.length) return null;
              return (
                <div key={cat} style={{marginBottom:28}}>
                  <div style={{fontSize:11,color:'#7A6555',textTransform:'uppercase',letterSpacing:'.1em',marginBottom:10}}>{cat}</div>
                  {catDishes.map(d=>(
                    <DishCard key={d.id} dish={d} isHost={isHost} isPast={isPast}
                      myRating={(d.dish_ratings||[]).find(r=>r.user_id===profile?.id)?.rating||0}
                      onApprove={()=>approveDish(d.id)}
                      onDecline={()=>declineDish(d.id)}
                      onRate={s=>rateDish(d.id,s)}
                      onEdit={d.guest_id===profile?.id&&!isPast?()=>setEditingDish(d):null}
                    />
                  ))}
                </div>
              );
            })}
            {(event.dishes||[]).length===0&&<div style={S.empty}><div style={{fontSize:52,marginBottom:16}}>🍳</div><h3 style={{fontFamily:'Cormorant Garamond,serif',fontSize:24,marginBottom:8}}>No dishes yet</h3><p style={{color:'#7A6555',fontSize:14}}>Guests can suggest what they'll bring.</p></div>}
          </div>
        )}

        {/* Guests */}
        {tab==='guests'&&(
          <GuestsTab event={event} isHost={isHost} onAddGuest={addGuest}
            onCancelAttendance={handleCancelAttendance} currentUserId={profile?.id}
            token={token} onRefresh={refresh}/>
        )}

        {/* Chat */}
        {tab==='chat'&&(
          <CommentsThread event={event}/>
        )}

        {/* Menu Balance */}
        {tab==='balance'&&(
          <div>
            <div style={{...S.card,...S.cardPad,marginBottom:20}}>
              <p style={{fontWeight:500,marginBottom:14}}>Course breakdown</p>
              <MenuBalance dishes={event.dishes||[]}/>
            </div>
            {pending.length>0&&(
              <div style={{...S.card,...S.cardPad,borderLeft:'3px solid #C4612A'}}>
                <p style={{fontSize:13,fontWeight:500,color:'#C4612A',marginBottom:12}}>Dishes awaiting your approval</p>
                {pending.map(d=>(
                  <div key={d.id} style={{display:'flex',alignItems:'center',gap:10,marginBottom:10}}>
                    <div style={{flex:1}}><p style={{fontSize:13,fontWeight:500}}>{d.name}</p><p style={{fontSize:11,color:'#7A6555'}}>{d.profiles?.full_name} · {d.category}</p></div>
                    <button style={{...S.btnSage,...S.btnSm}} onClick={()=>approveDish(d.id)}>Approve</button>
                    <button style={{background:'#FFEBEE',color:'#B71C1C',border:'none',borderRadius:8,padding:'6px 14px',fontSize:12,cursor:'pointer'}} onClick={()=>declineDish(d.id)}>Decline</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Wishlist */}
        {tab==='wishlist'&&(
  <div>
    <p style={{fontSize:13,color:'#7A6555',marginBottom:14}}>Dishes you'd love someone to bring — guests can claim these.</p>

    {/* Host add form */}
    {isHost && <WishlistAddForm eventId={event.id} onAdd={eventsHook.addWishlistItem} refresh={eventsHook.refresh}/>}

    {(event.wishlist_items||[]).length===0&&<p style={{fontSize:13,color:'#7A6555',fontStyle:'italic',marginTop:12}}>No wishlist items yet.</p>}
    {(event.wishlist_items||[]).map(w=>(
      <div key={w.id} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',borderRadius:10,background:'#FAF7F2',border:'1px solid #E5D9C8',marginBottom:8,opacity:w.claimed_by?0.65:1}}>
        <span style={{flex:1,fontSize:14}}>{w.description}</span>
        {w.claimed_by?<span style={{fontSize:11,padding:'4px 10px',borderRadius:20,background:'#E8F5E9',color:'#1B5E20'}}>Claimed by {w.profiles?.full_name}</span>
          :<button style={{...S.btnGhost,padding:'5px 12px',fontSize:12}} onClick={()=>claimWishlistItem(event.id,w.id)}>Claim this</button>}
      </div>
    ))}
  </div>
)}

        {/* APB */}
       {tab==='photos'&&(
  <EventPhotos event={event} currentUserId={profile?.id}/>
)}
{tab==='apb'&&!isPast&&(
  <APBPanel event={event} isHost={isHost} currentUserId={profile?.id}/>
)}

        {tab==='host'&&isHost&&(
          <HostDashboardPanel event={event}/>
        )}
        {/* Hall of Fame */}
        {tab==='hof'&&isPast&&(
          <HallOfFame dishes={event.dishes||[]} currentUserId={profile?.id} onRate={(did,s)=>rateDish(did,s)}/>
        )}
      </div>

      {/* Modals */}
      {showSuggest&&<SuggestDishModal prefill={instaPrefill} onClose={()=>{setShowSuggest(false);setInstaPrefill(null);}} onSuggest={handleSuggest}/>}
      {showSpoon&&<SpoonacularModal eventTheme={event.theme} onClose={()=>setShowSpoon(false)} onSelect={form=>{setInstaPrefill(form);setShowSpoon(false);setShowSuggest(true);}}/>}
      {/* MemoryCardModal removed */}
      {showEdit&&<EditEventModal event={event} onClose={()=>setShowEdit(false)} onSave={async form=>{await updateEvent(event.id,form);}}/>}

      {/* Edit dish modal — reuse SuggestDishModal pre-filled */}
      {editingDish&&(
        <SuggestDishModal
          prefill={{ name:editingDish.name, description:editingDish.description }}
          onClose={()=>setEditingDish(null)}
          onSuggest={handleEditDish}
        />
      )}
    </div>
  );
}
